import React, { useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { bool, shape, string, arrayOf, number, oneOfType, func, object, array } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { uniqueId, truncate } from 'lodash';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  DataTableSkeleton,
  Loading,
  Button,
} from '@carbon/react';
import { Pencil, Trash, Mail, CircleHelp, ArrowUpNarrowWide, ArrowDownWideNarrow, ArrowDownUp } from 'lucide-react';

import useNumberUtils from 'Hooks/useNumberUtils';
import UsersPagination from 'Containers/Users/UsersPagination';
import FilterUsers from 'Containers/Users/FilterUsers';
import { NWNotification, NWComboBox, NWTextInput } from 'Components/shared';
import { USER_ROLE } from 'Constants';

const StyledUsers = styled.div`
  .pageTitle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 2px solid #E2E2E2;

    .pageTitleActions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      align-items: center;

      button {
        cursor: pointer;
        background-color: transparent;
        border: none;
        padding: 0;

        svg {
          width: 24px;
          height: 30px;
        }
      }
    }
  }

  .cds--data-table {
    font-size: 16px;
    border: 1px solid #e2e2e2;

    thead {
      background: #e0e0e0;
      font-weight: 600;

      th {
        color: #3a3a3a;
        font-size: 16px;
        font-weight: 600;
        background: #f5f7f9;

        .cds--table-header-label {
          font-size: 16px;
          font-weight: 500;

          button {
            width: 100%;
            cursor: pointer;
            display: flex;
            align-items: center;
            background: transparent;
            border: none;
            gap: 5px;
            font-size: inherit;
            font-weight: inherit;

            svg {
              width: 18px;
              height: 18px;
              stroke: #707070;
              min-width: 18px;
            }
          }
        }
      }
    }

    tbody {
      tr {
        &:hover {
          background: #e5e5e5;

          td {
            background: inherit;
          }
        }

        &.inactive td {
          color: #999;
        }

        td {
          background: #fff;

          .actions {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 5px;

            .downloads {
              display: flex;
            }

            .hidden {
              visibility: hidden;
            }

            button {
              padding: 7px;
              border: 1px solid #dde6f2;
              cursor: pointer;

              &.deleteUser {
                background: #fef2e8;

                &:hover {
                  background: #fbb2ac;
                }
              }

              span {
                display: none;
              }

              .cds--loading {
                left: 0;
              }
            }

            a, button {
              display: flex;
              align-items: center;
              justify-content: center;
            }
          }
        }
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .cds--skeleton.cds--data-table-container {
      overflow: hidden;
      max-width: calc(100vw - 34px);
    }

    .pageTitle {
      margin-bottom: 20px;

      .pageTitleActions {
        > span {
          display: none;
        }
      }
    }

    .cds--data-table {
      border: none;

      > thead {
        display: none;
      }

      > tbody {
        background: #fff;

        tr {
          border: 1px solid #e2e2e2;
          position: relative;
          display: flex;
          flex-direction: column;
          height: auto;
          margin-top: 20px;

          &:hover {
            background: #fff;
          }

          &:first-of-type {
            &:hover td {
              border: none;
            }

            td {
              border: none;
            }
          }
          
          td {
            border: none;

            &:first-of-type {
              height: 40px;
              background: #F5F7F9;
            }

            .actions {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 0;
              padding: 10px 0;

              .hidden {
                overflow: hidden;
                flex: 0;
                width: 0;
                height: 0;
                margin-left: 0 !important;
              }

              > div {
                flex: 1;
                margin-left: 10px;

                button {
                  width: 100%;
                }
              }

              button {
                flex: 1;

                span {
                  display: inline-block;
                  margin-left: 10px;
                }
              }
            }
          }
        }
      }
    }
  }
`;

const StyledActions = styled.div`
  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e2e2e2;
    padding: 16px 0;
    margin-bottom: 16px;
  }

  .form {
    border: 1px solid #e2e2e2;
    margin-bottom: 20px;

    .headerTitle {
      display: flex;
      align-items: center;
      height: 48px;
      font-size: 1.25rem;
      color: #3A3A3A;
      background-color: #F5F7F9;
      text-align: left;
      padding: 0 14px;
    }

    .content {
      padding: 1rem;
      width: 100%;
      display: grid;
      gap: 2.25rem;
      grid-template-columns: repeat(2, minmax(0px, 1fr));

      .emailContainer {
        position: relative;

        > svg {
          position: absolute;
          top: 28px;
          right: 45px;
          z-index: 1;
        }

        .react-tooltip {
          max-width: 300px;
        }
      }
    }

    .footer {
      display: flex;
      padding: 0 20px 20px;

      > button {
        margin-right: 20px;
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .header {
      h1 {
        font-size: 24px;
        font-weight: 600;
      }

      .primary {
        font-size: 16px;
        border: none;
        width: auto;
        height: auto;

        span {
          font-size: 24px;
          padding-right: 5px;
        }

        &:hover {
          background-color: transparent;
        }
      }
    }

    .form {
      .content {
        grid-template-columns: auto;
        gap: 0;
        padding: 10px;

        .smallFields {
          grid-template-columns: auto;
          gap: 0;
        }

        .deliveryLabel {
          display: inline-block;
          margin-top: 15px;
        }
      }

      .footer {
        padding: 0 10px 20px;
        gap: 10px;

        > button {
          width: auto;
          flex: 1;
        }
      }
    }
  }
`;

const StyledButton = styled(Button)`
  width: 200px;
  height: 40px;
  padding: 0;
  justify-content: center;

  &.primary {
    border: 1px solid #1f59a2;
    color: #1f59a2;
  }

  &.danger {
    background-color: #da1e28;
    border: 1px solid transparent;
    color: #fff;
  }
`;

const StyledTableCell = styled(TableCell)`
    &[data-label="firstName"],
    &[data-label="lastName"] {
      word-break: break-word;
      padding: 10px 0;
    }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    &[data-label="userId"],
    &[data-label="email"],
    &[data-label="firstName"],
    &[data-label="lastName"],
    &[data-label="added"],
    &[data-label="userRole"] {    
      display: flex;
      align-items: center;
      min-height: 40px;
      padding-left: 130px;
      word-break: break-word;

      &:before {
        content: "${ props => props.name }:";
        position: absolute;
        left: 10px;
        width: 170px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 600;
      }
    }
  } 
`;

const fields = {
  firstName: 'firstName',
  lastName: 'lastName',
  language: 'language',
  status: 'status',
  email: 'email',
  password: 'password',
  confirmPassword: 'confirmPassword',
  removed: 'removed',
  userRole: 'userRole',
};

const UsersComponent = ({
  users,
  usersLoading,
  pagination,
  isActionsOpen,
  handleSubmit,
  isRequestPending,
  handleAddUser,
  handleEditUser,
  onDeleteUser,
  deleteUserIdLoading,
  handleSendEmail,
  emailUserIdLoading,
  sortOptions,
  defaultSortId,
  currentSort,
  handleMobileOrderChange,
  errors,
  edit,
  validators,
  register,
  rhfErrors,
  setValue,
  control,
  selectedLanguage,
  languageOptions,
  selectedStatus,
  statusOptions,
  selectedRemoved,
  removedOptions,
  userRoleOptions,
  selectedUserRole,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatDate } = useNumberUtils();
  const actionsRef = useRef(null);
  const isEditedUserAdmin = users.find(user => user.id === edit)?.userRole?.id === USER_ROLE.RESELLER_ADMIN;

  useEffect(() => {
    if (errors.length) {
      actionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errors]);

  const headerData = [
    {
      key: 'userId',
      header: t('pages.id'),
    },
    {
      key: 'email',
      header: t('pages.email'),
    },
    {
      key: 'firstName',
      header: t('pages.firstName'),
    },
    {
      key: 'lastName',
      header: t('pages.lastName'),
    },
    {
      key: 'added',
      header: t('pages.added'),
    },
    {
      key: 'userRole',
      header: t('pages.userRole'),
    },
    {
      key: 'actions',
      header: t('pages.actions'),
    },
  ];

  const mapHeaderToLabel = label => {
    let mappedLabel = label;
    if (label === 'userId') {
      mappedLabel = 'id';
    }
    return t(`pages.${mappedLabel}`);
  };

  const renderTableHeader = header => {
    const mappedSortingParams = {
      userId: 'user_id',
      email: 'email',
      firstName: 'first_name',
      lastName: 'last_name',
      added: 'added',
      userRole: 'user_role',
    };

    const [orderBy, orderDirection] = currentSort.id.split('-');
    const isSorted = orderBy === mappedSortingParams[header.key];
    const isAsc = orderDirection === 'asc';

    let sortIcon = <ArrowDownUp />;
    let sortDirection = 'asc';

    if (isSorted && currentSort.id !== defaultSortId) {
      sortIcon = isAsc ? <ArrowUpNarrowWide /> : <ArrowDownWideNarrow />;
      sortDirection = isAsc ? 'desc' : 'none';
    }

    if (isSorted && currentSort.id === defaultSortId) {
      sortIcon = <ArrowDownWideNarrow />;
      sortDirection = 'asc';
    }

    return (header.key === 'dropShipment' || header.key === 'actions') ? <span>{ header.header }</span> : (
      <button type="button" onClick={ () => handleMobileOrderChange(`${ mappedSortingParams[header.key] }-${ sortDirection }`) }>
        <span>{ header.header }</span>
        { sortIcon }
      </button>
    );
  };

  const renderCell = cell => {
    if (cell.info.header === 'added') {
      return formatDate(cell.value);
    }

    if (cell.info.header === 'userRole') {
      return cell.value.name;
    }

    if (cell.info.header === 'firstName' || cell.info.header === 'lastName') {
      return truncate(cell.value, { length: 21, omission: '...' });
    }

    if (cell.info.header === 'actions') {
      const { status, userId, removed } = cell.value;
      const uid = uniqueId();

      return (
        <div className="actions">
          <>
            <button
              id={ `editUser-${ uid }` }
              className="editUser"
              type="button"
              onClick={ () => {
                handleEditUser(userId);
                actionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
              } }
              disabled={ deleteUserIdLoading === userId }
            >
              <Pencil size={ 18 } />
            </button>
            <ReactTooltip
              className="editUser"
              anchorId={ `editUser-${ uid }` }
              variant="light"
              offset={ 10 }
              content={ (
                <p>{ t('common.edit') }</p>
              ) }
            />
            <div className={ removed ? 'hidden' : '' }>
              <button
                id={ `deleteUser-${ uid }` }
                className="deleteUser"
                type="button"
                onClick={ () => onDeleteUser(userId) }
                disabled={ deleteUserIdLoading === userId }
              >
                { deleteUserIdLoading === userId
                  ? <Loading active small withOverlay={ false } />
                  : <Trash size={ 18 } />
                }
              </button>
              <ReactTooltip
                className="deleteUser"
                anchorId={ `deleteUser-${ uid }` }
                variant="light"
                offset={ 10 }
                content={ (
                  <p>{ t('common.delete') }</p>
                ) }
              />
            </div>
            <div className={ !(status === 0) ? 'hidden' : '' }>
              <button
                id={ `sendEmail-${ uid }` }
                className="sendEmail"
                type="button"
                onClick={ () => handleSendEmail(userId) }
                disabled={ emailUserIdLoading === userId }
              >
                { emailUserIdLoading === userId
                  ? <Loading active small withOverlay={ false } />
                  : <Mail size={ 18 } />
                }
              </button>
              <ReactTooltip
                className="sendEmail"
                anchorId={ `sendEmail-${ uid }` }
                variant="light"
                offset={ 10 }
                content={ (
                  <p>{ t('orders.sendActivationEmail') }</p>
                ) }
              />
            </div>
          </>
        </div>
      );
    }

    return cell.value;
  };

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: !!rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    ...register(fieldName, { ...validators[fieldName] }),
  });

  const sharedComboBoxProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    control,
    rules: { ...validators[fieldName] }
  });

  return (
    <StyledUsers theme={ theme }>
      <StyledActions>
        <div ref={ actionsRef } className="header">
          <h1>{ t('pages.users') }</h1>
          <StyledButton
            className="primary"
            kind="ghost"
            onClick={ () => handleAddUser(!isActionsOpen) }
          >
            +{ t('pages.addUser') }
          </StyledButton>
        </div>
        <NWNotification errors={ errors } delay={ 10 } />
        { isActionsOpen && (
          <div className="form">
            <div className="headerTitle">
              <h4>{ t(edit ? 'pages.editUser' : 'pages.addUser') }</h4>
            </div>
            <div className="content">
              <section>
                <NWTextInput
                  light
                  required
                  labelText={ t(`pages.requestForm.${ fields.firstName }`) }
                  { ...sharedProps(fields.firstName) }
                />
                <NWTextInput
                  light
                  required
                  labelText={ t(`pages.requestForm.${ fields.lastName }`) }
                  { ...sharedProps(fields.lastName) }
                />
                <NWComboBox
                  key={ `language-${selectedLanguage?.id || 'none'}` }
                  titleText={ t(`pages.requestForm.${ fields.language }`) }
                  placeholder={ `${ t('common.select') } ${ t(`pages.requestForm.${ fields.language }`) }` }
                  light
                  required
                  { ...sharedComboBoxProps(fields.language) }
                  onChange={ ({ selectedItem }) => setValue(fields.language, selectedItem) }
                  items={ languageOptions || [] }
                  initialSelectedItem={ (languageOptions || []).find(item => item?.id === selectedLanguage?.id) || null }
                />
                { edit && (
                  <NWComboBox
                    key={ `status-${ edit ? 'edit' : 'new' }-${selectedStatus?.id || 'none'}` }
                    titleText={ t(`pages.requestForm.${ fields.status }`) }
                    placeholder={ `${ t('common.select') } ${ t(`pages.requestForm.${ fields.status }`) }` }
                    light
                    required
                    { ...sharedComboBoxProps(fields.status) }
                    onChange={ ({ selectedItem }) => setValue(fields.status, selectedItem) }
                    items={ statusOptions || [] }
                    initialSelectedItem={ (statusOptions || []).find(item => item?.id === selectedStatus?.id) || null }
                  />
                ) }
              </section>
              <section>
                <div className="emailContainer">
                  { (edit && isEditedUserAdmin) && (
                    <>
                      <CircleHelp id="emailTooltip" />
                      <ReactTooltip
                        className="emailTooltip"
                        anchorId="emailTooltip"
                        variant="light"
                        offset={ 10 }
                        content={ (
                          <p>{ t('orders.emailChangeInfo') }</p>
                        ) }
                      />
                    </>
                  ) }
                  <NWTextInput
                    light
                    required
                    readOnly={ edit && isEditedUserAdmin }
                    labelText={ t(`pages.requestForm.${ fields.email }`) }
                    { ...sharedProps(fields.email) }
                  />
                </div>
                { edit && (
                  <>
                    <NWTextInput
                      light
                      required
                      type="password"
                      labelText={ t(`pages.requestForm.${ fields.password }`) }
                      { ...sharedProps(fields.password) }
                    />
                    <NWTextInput
                      light
                      required
                      type="password"
                      labelText={ t(`pages.requestForm.${ fields.confirmPassword }`) }
                      { ...sharedProps(fields.confirmPassword) }
                    />
                    <NWComboBox
                      key={ `removed-${ edit ? 'edit' : 'new' }-${selectedRemoved?.id || 'none'}` }
                      titleText={ t(`pages.requestForm.${ fields.removed }`) }
                      placeholder={ `${ t('common.select') } ${ t(`pages.requestForm.${ fields.removed }`) }` }
                      light
                      required
                      { ...sharedComboBoxProps(fields.removed) }
                      onChange={ ({ selectedItem }) => setValue(fields.removed, selectedItem) }
                      items={ removedOptions || [] }
                      initialSelectedItem={ (removedOptions || []).find(item => item?.id === selectedRemoved?.id) || null }
                    />
                  </>
                ) }
                <NWComboBox
                  key={ `userRole-${ edit ? 'edit' : 'new' }-${selectedUserRole?.id || 'none'}` }
                  titleText={ t(`pages.requestForm.${ fields.userRole }`) }
                  placeholder={ `${ t('common.select') } ${ t(`pages.requestForm.${ fields.userRole }`) }` }
                  light
                  required
                  { ...sharedComboBoxProps(fields.userRole) }
                  disabled={ Number(selectedUserRole?.id) === USER_ROLE.RESELLER_ADMIN }
                  onChange={ ({ selectedItem }) => setValue(fields.userRole, selectedItem) }
                  items={ userRoleOptions || [] }
                  initialSelectedItem={ (userRoleOptions || []).find(item => item?.id === selectedUserRole?.id) || null }
                />
              </section>
            </div>
            <div className="footer">
              <StyledButton
                onClick={ handleSubmit }
                className="danger"
                kind="danger"
                disabled={ isRequestPending }
              >
                { isRequestPending && <Loading active={ isRequestPending } small withOverlay={ false } /> }
                { t('pages.save') }
              </StyledButton>
              <StyledButton
                kind="ghost"
                className="primary"
                onClick={ () => handleAddUser(false) }
              >
                { t('pages.cancel') }
              </StyledButton>
            </div>
          </div>
        )}
      </StyledActions>
      <FilterUsers
        sortOptions={ sortOptions }
        currentSort={ currentSort }
        handleMobileOrderChange={ handleMobileOrderChange }
      />
      { usersLoading ? (
        <DataTableSkeleton showToolbar={ false } showHeader={ false } />
      ) : (
        <DataTable rows={ users } headers={ headerData }>
          {({ rows, headers, getHeaderProps, getTableProps, getRowProps }) => (
            <TableContainer>
              <Table { ...getTableProps() }>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader
                        { ...getHeaderProps({ header }) }
                      >
                        { renderTableHeader(header) }
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  { users.length === 0 && (<TableRow><TableCell colSpan={ headers.length + 1 }>{ t('orders.noResultsFound') }</TableCell></TableRow>) }
                  {rows.map(row => (
                    <React.Fragment key={ row.id }>
                      <TableRow
                        className={ row.cells[6].value.status === 0 ? 'inactive' : '' }
                        { ...getRowProps({ row }) }
                      >
                        { row.cells.map(cell => (
                          <StyledTableCell
                            data-label={ cell.info.header }
                            name={ () => mapHeaderToLabel(cell.info.header) }
                            key={ cell.id }
                          >
                            { renderCell(cell) }
                          </StyledTableCell>
                        ))}
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}
      <UsersPagination loading={ usersLoading } { ...pagination } />
    </StyledUsers>
  );
};

UsersComponent.propTypes = {
  users: arrayOf(shape({
    id: oneOfType([number, string]),
    firstName: string,
    lastName: string,
    language: string,
    status: number,
    email: string,
    removed: bool,
  })),
  usersLoading: bool,
  pagination: shape({
    currentPage: number,
    recordsPerPage: number,
    totalPages: number,
    totalRecords: number,
    loading: bool,
  }),
  isActionsOpen: bool,
  handleSubmit: func,
  isRequestPending: bool,
  handleAddUser: func,
  handleEditUser: func,
  onDeleteUser: func,
  deleteUserIdLoading: string,
  handleSendEmail: func,
  emailUserIdLoading: string,
  sortOptions: array,
  defaultSortId: string,
  currentSort: object,
  handleMobileOrderChange: func,
  errors: array,
  edit: string,
  validators: object,
  register: func,
  rhfErrors: object,
  setValue: func,
  control: object,
  selectedLanguage: shape({
    id: oneOfType([number, string]),
    label: string,
  }),
  languageOptions: arrayOf(shape({
    id: oneOfType([number, string]),
    label: string,
  })),
  selectedStatus: shape({
    id: oneOfType([number, string]),
    label: string,
  }),
  statusOptions: arrayOf(shape({
    id: oneOfType([number, string]),
    label: string,
  })),
  selectedRemoved: shape({
    id: oneOfType([number, string]),
    label: string,
  }),
  removedOptions: arrayOf(shape({
    id: oneOfType([number, string]),
    label: string,
  })),
  userRoleOptions: arrayOf(shape({
    id: oneOfType([number, string]),
    label: string,
  })),
  selectedUserRole: shape({
    id: oneOfType([number, string]),
    label: string,
  }),
};

UsersComponent.displayName = 'UsersComponent';

export default UsersComponent;
